import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { custom } from 'devextreme/ui/dialog';
import { buildConfirmDialogTitleWithIcon } from 'src/app/shared/helpers/confirm-dialog.utils';

export interface ConfirmCloseConfirmOptions {
    headerIconClass?: string;
    withDetails?: boolean;
}

export interface ConfirmDialogButton {
    action: string;
    text: string;
    type?: 'normal' | 'default' | 'danger';
    stylingMode?: 'text' | 'outlined' | 'contained';
    elementAttr?: { class?: string };
}

type CustomDialogWithPopup = Parameters<typeof custom>[0] & {
    popupOptions?: Record<string, unknown>;
};

@Injectable({
    providedIn: 'root',
})
export class ConfirmDialogService {
    private readonly translocoService = inject(TranslocoService);

    activeConfirm: Promise<boolean> | null = null;
    activeChoiceConfirm: Promise<string | null> | null = null;
    activeDialog: ReturnType<typeof custom> | null = null;

    /**
     * Confirmation with **Close** (dismiss) and **Confirm** (destructive primary), right-aligned on a dark footer bar (Figma: delivery confirm).
     */
    confirmCloseConfirm(messageHtml: string, title: string, options?: ConfirmCloseConfirmOptions): Promise<boolean> {
        if (this.activeConfirm != null) return this.activeConfirm;

        const wrapperClasses = ['dx-dialog--close-confirm'];
        if (options?.withDetails) {
            wrapperClasses.push('dx-dialog--with-details');
        }
        if (options?.headerIconClass) {
            wrapperClasses.push('dx-dialog--with-header-icon');
        }

        const headerIconClass = options?.headerIconClass;

        const dialogOptions: CustomDialogWithPopup = {
            title,
            messageHtml,
            popupOptions: {
                wrapperAttr: { class: wrapperClasses.join(' ') },
                ...(headerIconClass
                    ? {
                          titleTemplate: () => buildConfirmDialogTitleWithIcon(title, headerIconClass),
                      }
                    : {}),
            },
            buttons: [
                {
                    text: this.translocoService.translate('close'),
                    type: 'normal',
                    stylingMode: 'outlined',
                    onClick: () => false,
                    elementAttr: {
                        class: 'dialog-btn-delivery-valid-close',
                    },
                },
                {
                    text: this.translocoService.translate('confirm'),
                    type: 'danger',
                    stylingMode: 'contained',
                    onClick: () => true,
                    elementAttr: {
                        class: 'dialog-btn-delivery-valid-confirm',
                    },
                },
            ],
        };

        this.activeDialog = custom(dialogOptions);

        this.activeConfirm = new Promise<boolean>(resolve => {
            this.activeDialog
                .show()
                .then(res => resolve(res === true))
                .catch(() => resolve(false));
        }).finally(() => {
            this.activeConfirm = null;
            this.activeDialog = null;
        });

        return this.activeConfirm;
    }

    /**
     * Confirmation dialog with custom buttons. Resolves to the clicked button id, or null when dismissed/cancelled.
     */
    confirmChoiceDialog(
        messageHtml: string,
        title: string,
        buttons: ConfirmDialogButton[],
        options?: ConfirmCloseConfirmOptions
    ): Promise<string | null> {
        if (this.activeChoiceConfirm != null) {
            return this.activeChoiceConfirm;
        }
        if (this.activeConfirm != null) {
            return Promise.resolve(null);
        }

        const wrapperClasses = ['dx-dialog--close-confirm'];
        if (options?.withDetails) {
            wrapperClasses.push('dx-dialog--with-details');
        }
        if (options?.headerIconClass) {
            wrapperClasses.push('dx-dialog--with-header-icon');
        }

        const headerIconClass = options?.headerIconClass;

        const dialogOptions: CustomDialogWithPopup = {
            title,
            messageHtml,
            popupOptions: {
                wrapperAttr: { class: wrapperClasses.join(' ') },
                ...(headerIconClass
                    ? {
                          titleTemplate: () => buildConfirmDialogTitleWithIcon(title, headerIconClass),
                      }
                    : {}),
            },
            buttons: buttons.map(button => ({
                text: button.text,
                type: button.type ?? 'normal',
                stylingMode: button.stylingMode ?? 'outlined',
                onClick: () => button.action,
                ...(button.elementAttr ? { elementAttr: button.elementAttr } : {}),
            })),
        };

        this.activeDialog = custom(dialogOptions);

        this.activeChoiceConfirm = new Promise<string | null>(resolve => {
            this.activeDialog
                .show()
                .then(res => resolve(typeof res === 'string' ? res : null))
                .catch(() => resolve(null));
        }).finally(() => {
            this.activeChoiceConfirm = null;
            this.activeDialog = null;
        });

        return this.activeChoiceConfirm;
    }

    confirmDialog(msg: string, title?: string, warning = false): Promise<boolean> {
        if (this.activeConfirm != null) return this.activeConfirm;

        this.activeDialog = custom({
            title: title ?? this.translocoService.translate('areYouSure'),
            messageHtml: msg,
            buttons: [
                {
                    text: warning ? this.translocoService.translate('confirm') : this.translocoService.translate('yes'),
                    onClick: () => true,
                    elementAttr: {
                        class: warning ? 'dialog-btn-warning' : 'dialog-btn-primary',
                    },
                },
                {
                    text: this.translocoService.translate('no'),
                    onClick: () => false,
                    elementAttr: {
                        class: 'dialog-btn-secondary',
                    },
                },
            ],
        });

        this.activeConfirm = new Promise<boolean>(resolve => {
            this.activeDialog
                .show()
                .then(res => resolve(res === true))
                .catch(() => resolve(false));
        }).finally(() => {
            this.activeConfirm = null;
            this.activeDialog = null;
        });

        return this.activeConfirm;
    }

    cancelActive(): void {
        if (!this.activeDialog) return;

        this.activeDialog.hide();
        this.activeDialog = null;
        this.activeConfirm = null;
        this.activeChoiceConfirm = null;
    }
}
